import { useState, useEffect } from "react";
import axios from "axios";
import ApiService from "../_services/ApiService";
import { useParams } from "react-router-dom";

const COMMON_FIELDS = [
  // "user",
  "campaign_type",
  "brand_logo_url",
  "brand_id",
  "brand_name",
  // "brand_logo",
];

const STEP_FIELDS = {
  0: [
    "title", "banner", "meta_description", "offer_description", "product_description",
    "offer_category", "url", "start_date", "end_date", "available_units", "delivery_time"
  ],
  1: [
    // "from_age", "to_age", 
    "gender", "redemption_steps", "offer_type",
    "audience_type", "influencer_tier", "cities", "platforms",
    // Affiliate commission. Sent on this step because it sits beside payout,
    // the campaign's other money field. `gnbrand_brand_public_id` is NOT here:
    // the backend stamps it from the session, so a brand can never attach its
    // rate to another brand's catalog.
    "commission_rate_type", "commission_rate_value",
  ],
  2: [
    "contact_name", "email", "phone",
    "content_do", "content_dont", "guidelines",
    "hashtags", "mentions", "faq", "content_review", "designation"
  ]
};

export const useMultiStepFormHandler = ({
  userData,
  campaignTypeFromUrl,
  activeStep,
  steps,
  history,
  successSnack,
  errorSnack,
  formSnackbar,
  isEditMode,
}) => {
  const [isDataFetched, setIsDataFetched] = useState(false);
  const { offerId } = useParams();
  const [formData, setFormData] = useState({
    title: "",
    banner: "",
    meta_description: "",
    offer_description: "",
    product_description: "",
    url: "",
    start_date: "",
    end_date: "",
    available_units: null,
    delivery_time: "",
    platforms: [],
    contact_name: "",
    email: "",
    phone: "",
    content_do: "",
    content_dont: "",
    guidelines: "",
    hashtags: "",
    mentions: "",
    faq: "",
    audience_type: [],
    influencer_tier: [],
    cities: [],
    offer_type: [],
    // from_age: null,
    // to_age: null,
    gender: "",
    redemption_steps: "",
    // Blank = this campaign offers no affiliate commission, which is the
    // default and what every campaign before this feature had.
    commission_rate_type: "",
    commission_rate_value: "",
    // user: userData?.id,
    campaign_type: campaignTypeFromUrl ? parseInt(campaignTypeFromUrl) : null,
    brand_logo_url: userData?.avatar_url,
    is_draft: true,
    brand_id: userData?.brand,
    brand_name: userData?.brand_name,
    // brand_logo: userData?.brand_logo,
    content_review: false,
    offer_category: '',
    designation: '',
  });

  const [barterId, setBarterId] = useState(null);
  const [loader, setLoader] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [productImageId, setProductImageId] = useState(null);
  const [uploadedProductImageUrls, setUploadedProductImageUrls] = useState([]);

  const fetchOfferDetails = async (barterId) => {
    setLoader(true);
    try {
      const res = await ApiService.get("all_campaigns", { id: barterId });
      if (res?.status === 200 || res?.status === 201) {
        const apiData = res?.data?.results[0];

        setFormData(prevData => ({
          ...prevData,
          ...apiData,
          campaign_type: apiData.campaign_type?.id || prevData.campaign_type || null,
          // user: prevData.user || apiData.user,
          brand_id: prevData.brand_id || apiData.brand_id,
          brand_name: prevData.brand_name || apiData.brand_name,
          // brand_logo: prevData.brand_logo || apiData.brand_logo,
          brand_logo_url: prevData.brand_logo_url || apiData.brand_logo_url,
        }));
        const productImageUrls = apiData?.product_images?.map(img =>
          typeof img === "string" ? img : img.url
        ) || [];
        const productImageIds = apiData?.product_images?.map(img => img.id).filter(Boolean) || [];
        setUploadedProductImageUrls(productImageUrls);
        setIsDataFetched(true);
        return { productImageUrls, productImageIds };
      }
    } catch (err) {
      errorSnack(formSnackbar.fetchError);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (campaignTypeFromUrl) {
      setFormData(prev => ({
        ...prev,
        campaign_type: parseInt(campaignTypeFromUrl)
      }));
    }
  }, [campaignTypeFromUrl]);

  const buildPayloadForStep = (step, bannerUrl) => {
    const fieldsToInclude = [...STEP_FIELDS[step], ...COMMON_FIELDS];
    const payload = {};
    console.debug("Building payload for step:", step, "with fields:", fieldsToInclude);
    console.debug("COMMON_FIELDS:", COMMON_FIELDS);

    for (const key of fieldsToInclude) {
      if (formData[key] !== undefined) {
        console.debug(`Adding field ${key} with value:`, formData[key]);
        payload[key] = formData[key];
      }
    }

    if (bannerUrl) {
      payload.banner = bannerUrl;
    }
    console.debug("Payload built for step:", step, "is:", payload);
    return transformFormData(payload);
  };

  const transformFormData = (data) => {
    const transformed = { ...data };

    const multiSelectFields = ["influencer_tier", "audience_type", "cities"];
    const labelOnlyFields = ["gender"]; // delivery_time handled below
    const numericFields = ["available_units", "from_age", "to_age", "payout"];

    multiSelectFields.forEach(f => {
      transformed[f] = data[f]?.map(item => item?.value ?? item);
    });

    labelOnlyFields.forEach(f => {
      if (data[f] && typeof data[f] === "object") {
        transformed[f] = data[f]?.label;
      }
    });

    if (data.delivery_time && typeof data.delivery_time === "object") {
      transformed.delivery_time = data.delivery_time.label;
    } else if (data.delivery_time) {
      transformed.delivery_time = data.delivery_time;
    }

    if (data.offer_category && typeof data.offer_category === "object") {
      transformed.offer_category = data.offer_category.value;
    } else if (data.offer_category !== undefined && data.offer_category !== null && data.offer_category !== "") {
      transformed.offer_category = data.offer_category;
    }


    if (Array.isArray(data.offer_type)) {
      transformed.offer_type = data.offer_type.map(item =>
        typeof item === "object" && item.value !== undefined ? item.value : item
      );
    } else if (data.offer_type && typeof data.offer_type === "object" && data.offer_type.value !== undefined) {
      transformed.offer_type = [data.offer_type.value];
    } else if (data.offer_type !== undefined && data.offer_type !== null && data.offer_type !== "") {
      transformed.offer_type = [data.offer_type];
    }

    numericFields.forEach(f => {
      if (data[f] !== "" && data[f] !== null && data[f] !== undefined) {
        transformed[f] = Number(data[f]);
      }
    });

    if (data.platforms && typeof data.platforms === "object" && !Array.isArray(data.platforms)) {
      transformed.platforms = Object.entries(data.platforms)
        .filter(([, value]) => value === true)
        .map(([key]) => parseInt(key));
    }
    if (data.campaign_type !== undefined && data.campaign_type !== null) {
      if (typeof data.campaign_type === "object" && data.campaign_type.value !== undefined) {
        transformed.campaign_type = parseInt(data.campaign_type.value);
      } else {
        transformed.campaign_type = parseInt(data.campaign_type);
      }
    }
    return transformed;
  };

  // const callProductImageAPI = async (barterId, productImageURLs = []) => {
  //   try {
  //     const payload = {
  //       product_images: [
  //         { barter: barterId },
  //         ...productImageURLs.map(url => ({ url }))
  //       ]
  //     };
  //     const response = await ApiService.post("product_images", payload);
  //     const productId = response?.data?.uploaded_files?.[0]?.id;
  //     if (productId) {
  //       setProductImageId(productId);
  //     }
  //     return response;
  //   } catch (error) {
  //     errorSnack("Failed to process product images");
  //     return null;
  //   }
  // };

  const callProductImageAPI = async (barterId, productImageURLs = []) => {
    try {
      const newUrls = productImageURLs.filter(
        url => !uploadedProductImageUrls.includes(url)
      );

      if (newUrls.length === 0) {
        console.debug("All product images already uploaded. Skipping API call.");
        // return;
        return { success: true, skipped: true };
      }

      const payload = {
        product_images: [
          { barter: barterId },
          ...newUrls.map(url => ({ url }))
        ]
      };

      const response = await ApiService.post("product_images", payload);
      const productId = response?.data?.uploaded_files?.[0]?.id;
      if (productId) {
        setProductImageId(productId);
      }

      setUploadedProductImageUrls(prev => [...prev, ...newUrls]);

      return response;
    } catch (error) {
      errorSnack("Failed to process product images");
      return null;
    }
  };

  const handleResponse = async (res, draft, isUpdate = false) => {
    setLoader(false);

    if (res.status === 200 || res.status === 201) {
      if (isUpdate && activeStep === 2) {
        successSnack(draft ? formSnackbar?.profileSuccess?.successMessageDraft : formSnackbar?.profileSuccess?.successMessage);
        history.push(draft ? "/new-offer/view-offer-drafts" : "/new-offer");
      }

      if (!isUpdate && activeStep === 2) {
        successSnack(draft ? formSnackbar?.profileSuccess?.successMessageDraft : formSnackbar?.profileSuccess?.successMessage);
        history.push(draft ? "/new-offer/view-offer-drafts" : "/new-offer");
      }

      if (!isEditMode && activeStep === 2) {
        successSnack(draft ? formSnackbar?.profileSuccess?.successMessageDraft : formSnackbar?.profileSuccess?.successMessage);
        history.push(draft ? "/new-offer" : "/new-offer");
      }

      if (activeStep === 2 && draft) {
        history.push(draft ? "/new-offer" : "/new-offer");
      }
    } else {
      const errorData = res?.data || {};
      Object.entries(errorData).forEach(([field, messages]) => {
        const msg = Array.isArray(messages) ? messages.join(", ") : JSON.stringify(messages);
        errorSnack(`${field}: ${msg}`);
      });
    }
  };

  const submitOfferDetails = async ({ bannerURL = null, draft = false, productImages = [] }) => {
    setLoader(true);
    let payload = buildPayloadForStep(activeStep, bannerURL);
    console.debug("Submitting offer details for step:", activeStep, "with payload:", payload);

    payload.is_draft = (activeStep === 2) ? draft : true;

    if (campaignTypeFromUrl && (!payload.campaign_type || isNaN(payload.campaign_type))) {
      payload.campaign_type = parseInt(campaignTypeFromUrl);
    }

    let response;
    let isUpdate = !!barterId;

    try {
      if (!isEditMode && !barterId) {
        response = await ApiService.post("barterCampaign", payload);
        const extractedId = response?.data?.data?.[0]?.fields?.id;
        if (extractedId) {
          localStorage.setItem("barterId", extractedId);
          setBarterId(extractedId);
          console.debug("New campaign created with ID:", extractedId);

          if (activeStep === 0 && !isEditMode) {
            await callProductImageAPI(extractedId, productImages);
          }

          const currentStep = steps[activeStep];
          history.replace(`/create-offer/${currentStep}/${campaignTypeFromUrl}`);
        }
      } else {
        const getBarterId = localStorage.getItem("barterId") || offerId || barterId;
        if (!getBarterId) {
          errorSnack("No campaign ID found for update. Please try again.");
          setLoader(false);
          return false;
        }

        // Routed through OUR backend, like the create above, instead of the
        // browser calling gncollege directly.
        //
        // This was a raw axios PUT to a hardcoded
        // https://college.groovenexus.com/... which was wrong three ways at
        // once:
        //
        //  1. it wrote to PRODUCTION from every environment, so a local or
        //     staging brand app silently edited live campaigns;
        //  2. it carried no Authorization header and never reached
        //     BarterCampaignView, so the backend's brand scoping did not run --
        //     any campaign id could be updated by anyone who knew it;
        //  3. it bypassed the server-side stamping of
        //     `gnbrand_brand_public_id`, which ties a campaign's affiliate rate
        //     to the right brand's catalog. Every step after the first is a
        //     PUT, so the commission fields never got it.
        //
        // `ApiService.put` sends the session token and resolves the host from
        // REACT_APP_BASEURL, so this now follows whatever environment it runs
        // in.
        response = await ApiService.put(
          "barterCampaign", payload, `id=${getBarterId}`,
        );
        console.debug("PUT response:", response);

        if (activeStep === 0 && productImages.length > 0) {
          await callProductImageAPI(getBarterId, productImages);
        }

        if (!isEditMode && !draft && activeStep >= 2) {
          localStorage.removeItem("barterId");
        }
      }

      await handleResponse(response, payload.is_draft, isUpdate);
      return true;
    } catch (error) {
      errorSnack("Something went wrong while submitting.");
      setLoader(false);
      return false;
    }
  };

  return {
    formData,
    setFormData,
    submitOfferDetails,
    loader,
    isDraft,
    setIsDraft,
    fetchOfferDetails,
    barterId,
    setBarterId,
    isDataFetched,
    uploadedProductImageUrls,
    setUploadedProductImageUrls
  };
};
